import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../sessionSlice';
import { fetchThemeData } from '../../themeSlice';
import { apiUrl } from '../../../utils/apiUrl';
import { getActiveGame } from '../../../utils/gameHelpers';
import './gameSelector.css';

const FALLBACK_CATALOG = [
  { id: 'cheers_to_peers', name: 'Cheers to Peers', description: 'Badge carousel, peer appreciation, gallery, and likes.' },
  { id: 'badges_of_trust', name: 'Badges of Trust', description: 'Media uploads, category feed, jigsaw puzzle, and moderation.' },
  { id: 'mens_day', name: "Men's Day", description: 'Men of the hour — uploads, social feed, and puzzle game.' },
  { id: 'every_boss_counts', name: 'Every Boss Counts', description: 'Flip-card boss archetypes, nominations, and social feed.' },
  { id: 'my_freedom', name: 'My Freedom', description: 'Freedom badges, story sharing, and social feed.' },
];

export default function GameSelector() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((s) => s.theme);

  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [selected, setSelected] = useState('cheers_to_peers');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchThemeData({ isAdmin: true }));
  }, [dispatch]);

  useEffect(() => {
    if (themeData) {
      setSelected(getActiveGame(themeData));
    }
  }, [themeData]);

  const loadCatalog = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const resp = await fetch(apiUrl('/getGameCatalog'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ currentTheme: currentTheme || themeData?.themename }),
      });
      const json = await resp.json();
      if (json.success) {
        if (Array.isArray(json.catalog)) setCatalog(json.catalog);
        if (json.activeGame) setSelected(json.activeGame);
      }
    } catch (e) {
      console.error('getGameCatalog', e);
    } finally {
      setLoading(false);
    }
  }, [adminToken, currentTheme, themeData?.themename]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleSave = async () => {
    if (!adminToken) {
      Swal.fire('Not signed in', 'Please sign in to the admin panel.', 'warning');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Activate game?',
      text: 'Only one game is shown on the player frontend at a time. Existing posts remain in the database.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Activate',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const resp = await fetch(apiUrl('/setActiveGame'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          activeGame: selected,
          currentTheme: currentTheme || themeData?.themename || 'theme0',
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) {
        Swal.fire('Error', json.error || 'Could not activate game.', 'error');
        return;
      }
      await dispatch(fetchThemeData({ isAdmin: true })).unwrap();
      Swal.fire('Saved', 'Active game updated. Players will see this game on /game.', 'success');
    } catch (e) {
      Swal.fire('Error', e.message || 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="game-selector-page">
      <h1>Active Game</h1>
      <p className="game-selector-desc">
        Choose which game players see after login. Content for each game can be edited in Rules and Customize.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="game-selector-grid">
          {catalog.map((game) => (
            <button
              key={game.id}
              type="button"
              className={`game-selector-card${selected === game.id ? ' selected' : ''}`}
              onClick={() => setSelected(game.id)}
            >
              <h3>{game.name}</h3>
              <p>{game.description}</p>
              {selected === game.id && <span className="game-selector-badge">Active</span>}
            </button>
          ))}
        </div>
      )}

      <div className="game-selector-actions">
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save active game'}
        </button>
        <button type="button" className="btn-secondary" onClick={() => navigate('/admin/rules')}>
          Edit game content
        </button>
      </div>
    </div>
  );
}
